import {
  filterVisibleSections,
  isRuntimeSectionDisabled,
} from '@framework/engine';
import {
  ModuleValidationStateIconBadge,
  SubmissionMenuDirtyBadge,
  focusOnSubmissionTitle,
} from '@framework/frontoffice-submission';
import { BrandedPath, focusNextElement, useHistory } from '@framework/ui';
import type {
  ItemType,
  MenuItemType,
  SubMenuType,
} from 'antd/es/menu/interface';
import { useMemo } from 'react';
import { useModuleFormSectionsMonitorContext } from '../../module/form/sections-monitor/ModuleFormSectionsMonitorContext';
import {
  ISubmissionMenuItem,
  SubmissionMenuItemType,
} from './ISubmissionMenuItem';
import { SubmissionMenuItem } from './SubmissionMenuItem';

/**
 * A partire da un array di oggetti di tipo IModuleMenuItem
 * li converte nelle Props per il componente Menu di Antd
 */
export function useSubmissionMenuPropsItems(params: {
  menu: ISubmissionMenuItem[];
  currentSubmissionModuleId: string | null | undefined;
  excludeSections?: boolean;
}): SubMenuType[] {
  const { menu, currentSubmissionModuleId, excludeSections } = params;
  const history = useHistory();

  const { runtimeSections } = useModuleFormSectionsMonitorContext();

  return useMemo(() => {
    return menu.map(mod => {
      const children =
        mod.children
          // Mostriamo la sezione solo se è visibile. Il calcolo viene fatto dal
          // FormHandler (vedi `ModuleFormSectionsMonitorContext`)
          ?.filter(i => {
            return filterVisibleSections({
              sectionId: i.context?.sectionId,
              submissionModuleId: i.context?.submissionModuleId,
              runtimeSections,
              currentSubmissionModuleId,
            });
          })
          .map(item => {
            const disabled = isRuntimeSectionDisabled(
              runtimeSections,
              item.context?.sectionId,
            );

            return createSubmenuItem({
              item,
              disabled,
              onClick: e => {
                e.domEvent.stopPropagation();
                e.domEvent.preventDefault();
                if (item.to) {
                  history.push(item.to as BrandedPath);

                  // In caso di Sezioni, facciamo focus sull'elemento successivo
                  // all'anchor che identifica la sezione.
                  // Gli anchor delle sezioni sono tutti del tipo `#sectionId`
                  if (item.type === SubmissionMenuItemType.Section) {
                    const anchorElement = document.getElementById(
                      item.to.substring(1),
                    );

                    focusNextElement(anchorElement);
                  }
                }
              },
            });
          }) ?? undefined;

      return {
        key: mod.key,
        icon: mod.icon,
        onClick: e => {
          // Se l'evento è stato già gestito da un MenuItem _interno_,
          // non effettuiamo il redirect: vuol dire che l'utente vuole
          // visitare una sezione interna al modulo.
          if (mod.to && !e.domEvent.isDefaultPrevented()) {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            history.push(mod.to as BrandedPath);
            setTimeout(() => {
              // focus sul titolo pagina per accessibilità
              focusOnSubmissionTitle();
            }, 100);
          }
        },
        // onTitleClick: info => {
        //   console.log('onTitleClick > mod.to', mod.to);
        //   if (mod.to) {
        //     window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        //     console.log('onTitleClick > mod.to', mod.to);
        //     console.log('onTitleClick > mod.to info', info);
        //     history.push(mod.to as BrandedPath);
        //   }
        // },
        label: (
          <SubmissionMenuItem
            item={mod}
            badge={
              <ModuleValidationStateIconBadge
                type={mod.context?.state}
                validationState={mod.context?.validationState}
                hideIfNoOpen
              />
            }
          />
        ),
        children:
          !excludeSections && children && children.length > 0
            ? children
            : undefined,
      } as SubMenuType;
    });
  }, [
    menu,
    history,
    currentSubmissionModuleId,
    runtimeSections,
    excludeSections,
  ]);
}

function createSubmenuItem(input: {
  item: ISubmissionMenuItem;
  disabled: boolean;
  onClick: MenuItemType['onClick'];
}): ItemType<MenuItemType> {
  const { item, disabled, onClick } = input;
  return {
    key: item.key,
    disabled,
    onClick,
    // onTitleClick: info => onClick,
    icon: item.type === SubmissionMenuItemType.Section ? undefined : item.icon,

    label: (
      <SubmissionMenuItem
        item={item}
        disabled={disabled}
        badge={
          <SubmissionMenuDirtyBadge
            sectionId={item.key}
            submissionModuleId={item.context?.submissionModuleId}
          />
        }
      />
    ),
  };
}
